import React, { useState, useEffect } from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Modal, Typography } from 'components';
import { IcoClose } from 'icons/indexed-icons/IcoClose';
import { CloseIcon } from 'pages/UserManagementTwo/components/User/UpdateUser';
import { ModalSize } from 'types';
import { Location } from 'history';
import theme from 'themes';

type ModalHeaderWrapperProps = {
  $variant?: 'danger' | 'warning' | 'info' | 'default';
};

const ModalHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0 1rem;
  width: 100%;
  gap: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  font-family: Roboto;
  color: ${theme.colors.textLightBlack};
`;
const ModalContent = styled.div<{ flexDirection?: string; margin?: string }>`
  flex-direction: ${({ flexDirection }) => flexDirection || 'row'};
  padding: 0 1rem;
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-left: 2.3rem;
  flex: 1;
  font-family: Roboto;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${theme.colors.lightGrey8};
  margin: ${({ margin }) => margin};
`;
const ModalCloseButton = styled.div`
  margin-left: auto;
`;
const StyledButtonRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1.5rem;
  width: 65%;
  margin-left: auto;
`;

const IconWrapper = styled.span<ModalHeaderWrapperProps>`
  .icon {
    margin-bottom: -4px;
    height: 20px;
    width: 20px;
    color: ${({ $variant }) => {
      switch ($variant) {
        case 'danger':
          return theme.colors.text.error;
        case 'warning':
          return `${theme.colors.caution200}`;
        case 'info':
        case 'default':
        default:
          return theme.colors.primaryBlue5;
      }
    }};
  }
`;

type Props = {
  title: JSX.Element | string;
  children?: JSX.Element | string;
  isVisible: boolean;
  isConfirmPrompt?: boolean;
  onCancelCallback?: () => void;
  onConfirmCallback?: () => void;
  headerIcon?: JSX.Element | string;
  primaryBtnText?: string;
  secondaryBtnText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'default';
  type?: string;
  btnWidth?: string;
  hideCancelButton?: boolean;
  isBackdropClose?: boolean;
  zIndex?: number;
  isOverLandscapeModal?: boolean;
  height?: string;
  margin?: string;
  size?: 'small' | 'medium' | 'large';
  disableConfirmBtn?: boolean;
};

const ConfirmationModal: React.FC<Props> = ({
  title,
  children,
  isConfirmPrompt,
  isVisible,
  onCancelCallback,
  onConfirmCallback,
  headerIcon,
  secondaryBtnText = 'Cancel',
  primaryBtnText = 'Confirm',
  variant = 'warning',
  btnWidth,
  hideCancelButton = false,
  isBackdropClose = true,
  zIndex,
  isOverLandscapeModal = false,
  height,
  margin,
  size = 'small',
  disableConfirmBtn = false
  // type = 'warning'
}) => {
  const history = useHistory();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const modalHeight = height || (size === 'small' ? '12rem' : size === 'medium' ? '80%' : '90%');

  useEffect(() => {
    isVisible && setIsConfirmed(false);
  }, [isVisible]);

  // useEffect(() => {
  //   setIsModalOpen(isVisible);
  // }, [isVisible]);

  const handleBlocked = (nextLocation?: Location) => {
    if (!isConfirmed && isVisible) {
      setIsModalOpen(true);
      nextLocation && setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isConfirmPrompt && isVisible) {
      handleBlocked();
    }
  }, [isConfirmPrompt, isVisible]);

  const handleConfirm = () => {
    setIsModalOpen(isVisible);
    setIsConfirmed(true);
  };

  useEffect(() => {
    if (isConfirmed) {
      onConfirmCallback?.();
      lastLocation &&
        history.push({
          pathname: lastLocation.pathname,
          search: lastLocation.search // Include the search parameters
        });
      setIsConfirmed(false);
    }
  }, [isConfirmed, lastLocation]);

  return (
    <>
      <Prompt when={isVisible} message={handleBlocked} />
      <Modal
        bgOpacity={'78%'}
        isBackdropClose={isBackdropClose}
        backgroundColor="black"
        isLandscape={isOverLandscapeModal}
        title={
          <ModalHeaderWrapper>
            {headerIcon && <IconWrapper $variant={variant}>{headerIcon}</IconWrapper>}
            <Typography weight="bold" size={'1.25rem'} mb={0}>
              {title}
            </Typography>
            {hideCancelButton === true ? null : (
              <ModalCloseButton>
                <CloseIcon
                  onClick={() => {
                    setIsModalOpen(false);
                    onCancelCallback?.();
                  }}
                >
                  {IcoClose()}
                </CloseIcon>
              </ModalCloseButton>
            )}
          </ModalHeaderWrapper>
        }
        allowContentScroll={false}
        onClose={() => {
          setIsModalOpen(false);
          onCancelCallback?.();
        }}
        showCloseButton={false}
        heightOverride={modalHeight}
        size={
          size === 'small'
            ? ModalSize.XSMALL
            : size === 'medium'
            ? ModalSize.MEDIUM
            : ModalSize.LARGE
        }
        visible={isModalOpen}
        zIndex={zIndex || (isOverLandscapeModal ? 999 : undefined)}
      >
        <ModalContent margin={margin}>{children}</ModalContent>
        <StyledButtonRow>
          {hideCancelButton === true ? null : (
            <Button
              onClick={() => {
                setIsModalOpen(false);
                onCancelCallback?.();
              }}
              size="small"
              variant={
                variant === 'danger'
                  ? 'outline-danger'
                  : variant === 'info' || variant === 'default'
                  ? 'outline-blue'
                  : 'outline-generic'
              }
              borderColor={variant === 'warning' ? `${theme.colors.caution200}` : ''}
              color={variant === 'warning' ? `${theme.colors.caution200}` : ''}
              width={btnWidth}
            >
              {secondaryBtnText}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={disableConfirmBtn}
            size="small"
            variant={
              variant === 'danger' || variant === 'warning' ? 'filled-generic' : 'filled-primary'
            }
            bgColor={
              variant === 'warning'
                ? `${theme.colors.caution200}`
                : variant === 'danger'
                ? `${theme.colors.text.error}`
                : ''
            }
            width={btnWidth}
          >
            {primaryBtnText}
          </Button>
        </StyledButtonRow>
      </Modal>
    </>
  );
};

export default ConfirmationModal;
